import ApiServer from '@/utils/api/ApiServer.js'
import { saveAs } from 'file-saver'
import moment from 'moment'
import { SettlementKeys, SettlementSubKeys } from '@/components/settlements/SettlementKeys.js'
import store from '@/store'
import { ebDocumentArchiveClient } from '@/utils/ExportBatchDocumentArchiveClient'
import { EbArchivePathSegments, EbArchiveSegmentBySubmoduleKey } from '@/components/settlements/ExportBatchArchivePathSegments'
import i18n from '@/i18n'
import { ErrorSource, SettlementArchiveStatus, AccountingCategory, SettlementCsvExportType } from '@/utils/Enumerations.js'
import { localeCompareAlphanumeric } from '@/utils/base'

export default {
  async refreshModuleData (context, setLoading = true) {
    const { currentModuleKey, dateConfiguration, currentSubModuleKey } = context.getters
    try {
      if (setLoading) context.commit('setLoading', true)

      if (!dateConfiguration.fiscalYear || !dateConfiguration.payPeriod) { return }

      let data

      switch (currentModuleKey) {
        case SettlementKeys.AccountsPayable:
          data = await refreshAccountsPayableModule(dateConfiguration, currentSubModuleKey)
          break
        case SettlementKeys.AccountsReceivable:
          data = await refreshAccountsReceivableModule(dateConfiguration, currentSubModuleKey)
          break
        case SettlementKeys.Accruals:
          data = await refreshAccrualsModule(dateConfiguration, currentSubModuleKey)
          break
      }

      if (currentSubModuleKey === SettlementSubKeys.AP.SettlementsByContract) {
        context.commit('initializeContractStatuses', {
          settlements: data.settlements,
          payPeriods: store.getters['pay-period/allPayPeriods']
        })
      }

      context.commit('setModuleData', {
        moduleKey: currentModuleKey,
        subModuleKey: currentSubModuleKey,
        data: data
      })
    } catch (e) {
      console.error(e)
      store.dispatch('export-batch/fetchExportBatches', dateConfiguration.payPeriod.payPeriodId)
    } finally {
      context.commit('setLoading', false)
    }
  },

  async fetchSettlementVerifications (context, type) {
    const { fiscalYear, payPeriod, exportBatch } = context.getters.dateConfiguration
    const isExportBatch = exportBatch.exportBatchId !== undefined
    if (isExportBatch) return []
    const path = `fiscalYears/${fiscalYear.fiscalYearId}/payPeriods/${payPeriod.payPeriodId}/settlementVerifications`
    const url = ApiServer.urlFor(path, { version: 1, params: { type } })
    const { data: settlementVerifications } = await ApiServer.get(url)
    return settlementVerifications
  },

  async createSettlementVerification (context, requestObject) {
    const { fiscalYear, payPeriod, exportBatch } = context.getters.dateConfiguration
    const isExportBatch = exportBatch.exportBatchId !== undefined
    if (isExportBatch) throw Error('Cannot create settlement verification for export batch')
    const path = `fiscalYears/${fiscalYear.fiscalYearId}/payPeriods/${payPeriod.payPeriodId}/settlementVerifications`
    const url = ApiServer.urlFor(path, { version: 1 })
    const response = await ApiServer.post(url, requestObject)
    return response.data
  },

  async deleteSettlementVerification (context, settlementVerificationId) {
    const { fiscalYear, payPeriod, exportBatch } = context.getters.dateConfiguration
    const isExportBatch = exportBatch.exportBatchId !== undefined
    if (isExportBatch) throw Error('Cannot delete settlement verification for export batch')
    const path = `fiscalYears/${fiscalYear.fiscalYearId}/payPeriods/${payPeriod.payPeriodId}/settlementVerifications/${settlementVerificationId}`
    const url = ApiServer.urlFor(path, { version: 1 })
    const response = await ApiServer.delete(url)
    return response.data
  },

  async updateSettlementVerification (context, requestObject) {
    const { settlementVerificationId } = requestObject
    const { fiscalYear, payPeriod, exportBatch } = context.getters.dateConfiguration
    const isExportBatch = exportBatch.exportBatchId !== undefined
    if (isExportBatch) throw Error('Cannot create settlement verification for export batch')
    const path = `fiscalYears/${fiscalYear.fiscalYearId}/payPeriods/${payPeriod.payPeriodId}/settlementVerifications`
    const url = ApiServer.urlFor(`${path}/${settlementVerificationId}`, { version: 1 })
    const response = await ApiServer.put(url, requestObject)
    return response.data
  },

  async fetchSettlementEmailRecords (context, isByproduct) {
    const { payPeriod, exportBatch } = context.getters.dateConfiguration
    if (exportBatch.exportBatchId === undefined) return []
    const path = `payPeriods/${payPeriod.payPeriodId}/exportBatches/${exportBatch.exportBatchId}/emailrecords`
    const url = ApiServer.urlFor(path, { version: 2, params: { isByproduct } })
    const { data: emailRecords } = await ApiServer.get(url)
    return emailRecords
  },

  async fetchSettlementsByPayee (context) {
    const { fiscalYear, payPeriod, exportBatch } = context.getters.dateConfiguration
    const isExportBatch = exportBatch.exportBatchId !== undefined
    const path = isExportBatch
      ? `payPeriods/${payPeriod.payPeriodId}/exportBatches/${exportBatch.exportBatchId}/settlementsByPayee`
      : `fiscalYears/${fiscalYear.fiscalYearId}/payPeriods/${payPeriod.payPeriodId}/settlementsByPayee`
    const url = ApiServer.urlFor(path, { version: isExportBatch ? 2 : 1 })
    const { data: settlementsByPayee } = await ApiServer.get(url)
    return settlementsByPayee
  },

  async fetchByproductSettlementsByPayee (context) {
    const { fiscalYear, payPeriod, exportBatch } = context.getters.dateConfiguration
    const isExportBatch = exportBatch.exportBatchId !== undefined
    const path = isExportBatch
      ? `payPeriods/${payPeriod.payPeriodId}/exportBatches/${exportBatch.exportBatchId}/ByproductSettlementsByPayee`
      : `fiscalYears/${fiscalYear.fiscalYearId}/payPeriods/${payPeriod.payPeriodId}/ByproductSettlementsByPayee`

    const options = { version: isExportBatch ? 2 : 1 }
    const url = ApiServer.urlFor(path, options)
    const response = await ApiServer.get(url)
    return response.data
  },

  async fetchJESettlementsByPayee (context) {
    const { fiscalYear, payPeriod, exportBatch } = context.getters.dateConfiguration
    const isExportBatch = exportBatch.exportBatchId !== undefined
    const path = isExportBatch
      ? `payPeriods/${payPeriod.payPeriodId}/exportBatches/${exportBatch.exportBatchId}/journalentrysettlementsbypayee`
      : `fiscalYears/${fiscalYear.fiscalYearId}/payPeriods/${payPeriod.payPeriodId}/journalentrysettlementsbypayee`

    const options = { version: isExportBatch ? 2 : 1 }
    const url = ApiServer.urlFor(path, options)
    const response = await ApiServer.get(url)
    return response.data
  },

  async getTicketsForPeriod (context, tractIds) {
    const headers = {
      tractIds: tractIds
    }
    const { fiscalYear, payPeriod } = context.getters.dateConfiguration
    const path = `v2/fiscalYears/${fiscalYear.fiscalYearId}/payPeriods/${payPeriod.payPeriodId}/tickets`
    const response = await ApiServer.get(path, {
      headers: headers
    })
    return response.data
  },

  async getTractsForPeriod (context) {
    const { fiscalYear, payPeriod } = context.getters.dateConfiguration
    const path = `v2/fiscalYears/${fiscalYear.fiscalYearId}/payPeriods/${payPeriod.payPeriodId}/tracts`
    const response = await ApiServer.get(path)
    return response.data
  },

  setCurrentDateConfiguration (context, dateConfiguration) {
    context.commit('setDateConfiguration', dateConfiguration)
  },

  setCurrentModule (context, moduleConfiguration) {
    context.commit('setCurrentModule', moduleConfiguration)
  },

  resetModules (context) {
    const defaultModuleConfiguration = {
      moduleKey: SettlementKeys.AccountsPayable,
      subModuleKey: SettlementSubKeys.AP.ProductionSummary
    }
    context.commit('setCurrentModule', defaultModuleConfiguration)
    context.commit('resetModules')
  },

  async fetchSettlementCSV (_, { payPeriod, fiscalYear, exportBatch, type, isByproduct }) {
    const isExportBatch = exportBatch.exportBatchId !== undefined
    const path = isExportBatch ? `payPeriods/${payPeriod.payPeriodId}/exportBatches/${exportBatch.exportBatchId}/${type}` : `fiscalYears/${fiscalYear.fiscalYearId}/payPeriods/${payPeriod.payPeriodId}/${type}`
    const options = {
      version: isExportBatch ? 2 : 1,
      params: { byproduct: isByproduct }
    }
    const url = ApiServer.urlFor(path, options)
    const response = await ApiServer.get(url, { headers: { 'Content-Type': 'text/csv' } })
    generateCSV(response.data, type, exportBatch)
    return response.data
  },

  async backpopulateExportBatchArchive (_, { payPeriodId, exportBatchId }) {
    const url = ApiServer.urlFor(`payperiods/${payPeriodId}/exportBatches/${exportBatchId}/rebuildarchive`, { version: 2 })
    const response = await ApiServer.post(url)
    return response.data
  },

  toggleContractStatus (context, contractId) {
    context.commit('toggleContractStatus', contractId)
  },

  clearStaleSubModuleData (context, { isByproduct, allData }) {
    context.commit('clearStaleSubModuleData', { isByproduct: isByproduct, allData: allData })
  },

  async createSettlementEmailRequest (context, { accountId, accountContactId }) {
    const { dateConfiguration, currentSubModuleKey } = context.getters
    const { exportBatch, payPeriod } = dateConfiguration
    const isByproduct = currentSubModuleKey === SettlementSubKeys.AP.ByproductSettlementsByPayee

    const url = ApiServer.urlFor(`payPeriods/${payPeriod.payPeriodId}/exportBatches/${exportBatch.exportBatchId}/emailrequest`, { version: 2 })
    const response = await ApiServer.post(url, { accountId, accountContactId, isByproduct })
    return response.data
  },

  async fetchSubmoduleBlob (context, { accountId = undefined }) {
    const { dateConfiguration, currentSubModuleKey } = context.getters
    const ebId = dateConfiguration?.exportBatch?.exportBatchId
    const archivePathSegment = EbArchiveSegmentBySubmoduleKey(currentSubModuleKey)
    let archivepath = `${ebId}/${archivePathSegment}`
    if (accountId) archivepath = `${archivepath}/${accountId}`
    const blob = await ebDocumentArchiveClient.fetchDocumentFromBlobStorage(archivepath)
    return blob
  },

  async downloadSubmoduleBlob (context, { accountId = undefined, filename }) {
    const { dateConfiguration, currentSubModuleKey } = context.getters
    if (dateConfiguration?.exportBatch?.settlementArchiveStatus !== SettlementArchiveStatus.Success.value) {
      store.dispatch('snackbar/setSnack', i18n.t('documentGenerationInProgress'))
      return
    }

    try {
      const ebId = dateConfiguration?.exportBatch?.exportBatchId
      const archivePathSegment = EbArchiveSegmentBySubmoduleKey(currentSubModuleKey)
      let archivepath = `${ebId}/${archivePathSegment}`
      if (accountId) archivepath = `${archivepath}/${accountId}`
      const blob = await ebDocumentArchiveClient.fetchDocumentFromBlobStorage(archivepath)
      saveAs(blob, filename)
    } catch (e) {
      console.error(e)
      snackDocumentReadError()
    }
  },

  async fetchGLReconciliation (_, { payPeriodId, exportBatchId }) {
    if (payPeriodId === undefined || payPeriodId === null) return undefined

    const path = exportBatchId
      ? `payPeriods/${payPeriodId}/exportBatches/${exportBatchId}/glreconciliation`
      : `payPeriods/${payPeriodId}/glreconciliation`

    const response = await ApiServer.get(ApiServer.urlFor(path, { version: 2 }))
    return response.data
  },

  async downloadGLReconciliationPdf (_, { payPeriod, exportBatch, filename }) {
    if (exportBatch) {
      if (exportBatch.settlementArchiveStatus !== SettlementArchiveStatus.Success.value) {
        store.dispatch('snackbar/setSnack', i18n.t('documentGenerationInProgress'))
        return
      }

      try {
        const blob = await ebDocumentArchiveClient.fetchDocumentFromBlobStorage(`${exportBatch.exportBatchId}/${EbArchivePathSegments.GLReconciliation}`)
        saveAs(blob, filename)
      } catch (e) {
        console.error(e)
        if (e?.statusCode !== 404) {
          snackDocumentReadError()
          return
        }
        // Requesting the data triggers the server's lazy rebuild of the missing archive.
        store.dispatch('settlements/fetchGLReconciliation', {
          payPeriodId: payPeriod.payPeriodId,
          exportBatchId: exportBatch.exportBatchId
        }).catch(() => {})
        store.dispatch('snackbar/setSnack', i18n.t('documentGenerationInProgress'))
      }
      return
    }

    try {
      const blob = await ebDocumentArchiveClient.fetchPayPeriodDocumentFromBlobStorage(`${payPeriod.payPeriodId}/${EbArchivePathSegments.GLReconciliation}`)
      saveAs(blob, filename)
    } catch (e) {
      console.error(e)
      if (e?.statusCode === 404) {
        store.dispatch('snackbar/setSnack', i18n.t('documentGenerationInProgress'))
        return
      }
      snackDocumentReadError()
    }
  }
}

const snackDocumentReadError = () => {
  store.dispatch('snackbar/setSnackError', {
    message: i18n.t('errorReadingDocumentFromBlobStorage'),
    code: 'DGP01',
    source: ErrorSource.WebClient
  })
}

const refreshAccountsPayableModule = async (dateConfiguration, currentSubModuleKey) => {
  const { fiscalYear, payPeriod, exportBatch } = dateConfiguration
  const isExportBatch = exportBatch.exportBatchId !== undefined
  let path = isExportBatch
    ? `payPeriods/${payPeriod.payPeriodId}/exportBatches/${exportBatch.exportBatchId}/`
    : `fiscalYears/${fiscalYear.fiscalYearId}/payPeriods/${payPeriod.payPeriodId}/`

  const options = {
    version: isExportBatch ? 2 : 1,
    params: {
      byproduct: (
        currentSubModuleKey === SettlementSubKeys.AP.BPPaymentRegister ||
        currentSubModuleKey === SettlementSubKeys.AP.ByproductCorrections) ? true : undefined
    }
  }

  switch (currentSubModuleKey) {
    case SettlementSubKeys.AP.SettlementsByContract:
      path += 'settlementsByContract'
      break
    case SettlementSubKeys.AP.PaymentRegister:
      path += 'paymentRegister'
      break
    case SettlementSubKeys.AP.BPPaymentRegister:
      path += 'paymentRegister'
      break
    case SettlementSubKeys.AP.ProductionSummary:
      path += isExportBatch ? 'productionSummary' : 'producerProduction'
      break
    case SettlementSubKeys.AP.TicketRegister:
      path += 'ticketRegister'
      break
    case SettlementSubKeys.AP.SettlementsByPayee:
      path += 'settlementsByPayee'
      break
    case SettlementSubKeys.AP.TractPayments:
      path += 'tractPayments'
      break
    case SettlementSubKeys.AP.Advances:
      path += 'advances'
      options.version = 2
      break
    case SettlementSubKeys.AP.Corrections:
    case SettlementSubKeys.AP.ByproductCorrections:
      path += 'corrections'
      options.version = 2
      break
    case SettlementSubKeys.AP.ByproductSettlementsByPayee:
      path += 'byproductSettlementsByPayee'
      break
    case SettlementSubKeys.AP.AccountPayments:
      path += 'accountPayments'
      break
    case SettlementSubKeys.AP.MiscPayments:
      const [tractPayments, advances, accountPayments] = await Promise.all([
        fetchData(path + 'tractPayments', options),
        fetchData(path + 'advances', { ...options, version: 2 }),
        fetchData(path + 'accountPayments', { ...options, params: { ...options.params, categoryType: AccountingCategory.Payable.value } })
      ])
      return { tractPayments, advances, accountPayments }
  }

  const resultData = await fetchData(path, options)
  sortResultData(resultData, currentSubModuleKey)
  return resultData
}

const fetchData = async (path, options) => {
  const url = ApiServer.urlFor(path, options)
  return (await ApiServer.get(url)).data
}

function sortResultData (resultData, currentSubModuleKey) {
  try {
    switch (currentSubModuleKey) {
      case SettlementSubKeys.AP.ProductionSummary:
      case SettlementSubKeys.AR.ProductionSummary: {
        resultData.production.sort((a, b) => localeCompareAlphanumeric(a?.name, b?.name))
        resultData.productSummary.sort((a, b) => localeCompareAlphanumeric(a?.productName, b?.productName))
        break
      }
    }
  } catch (e) {
    console.error(`Failed to sort data. ${e}`)
  }
}

const refreshAccountsReceivableModule = async (dateConfiguration, currentSubModuleKey) => {
  const { fiscalYear, payPeriod, exportBatch } = dateConfiguration
  const isExportBatch = exportBatch.exportBatchId !== undefined
  let path = isExportBatch
    ? `payPeriods/${payPeriod.payPeriodId}/exportBatches/${exportBatch.exportBatchId}/`
    : `fiscalYears/${fiscalYear.fiscalYearId}/payPeriods/${payPeriod.payPeriodId}/`

  const params = { byproduct: SettlementSubKeys.AR.BPReceivableRegister === currentSubModuleKey }
  switch (currentSubModuleKey) {
    case SettlementSubKeys.AR.ReceivableRegister:
      path += 'receivableRegister'
      break
    case SettlementSubKeys.AR.BPReceivableRegister:
      path += 'receivableRegister'
      break
    case SettlementSubKeys.AR.BPReceivablesByPayer:
      path += 'byproductARByCounterparty'
      break
    case SettlementSubKeys.AR.BPReceivableSummary:
      path += 'byproductReceivableSummary'
      break
    case SettlementSubKeys.AR.ProductionSummary:
      path += isExportBatch ? 'receivableSummary' : 'arProductionSummary'
      break
    case SettlementSubKeys.AR.SalesAnalysis:
      path += isExportBatch ? 'salesAnalysis' : 'arSalesAnalysis'
      const businessEntityId = store.getters['settlements/arSalesAnalysisBusinessEntity'].businessEntityId
      params.businessEntityId = businessEntityId
      break
  }

  const options = { version: isExportBatch ? 2 : 1, params: params }
  const url = ApiServer.urlFor(path, options)

  const result = await ApiServer.get(url)
  sortResultData(result.data, currentSubModuleKey)
  return result.data
}

const refreshAccrualsModule = async (dateConfiguration, currentSubModuleKey) => {
  const { fiscalYear, payPeriod, exportBatch } = dateConfiguration
  const isExportBatch = exportBatch.exportBatchId !== undefined
  const options = {
    version: isExportBatch ? 2 : 1
  }

  let path = exportBatch.exportBatchId
    ? `payPeriods/${payPeriod.payPeriodId}/exportBatches/${exportBatch.exportBatchId}/`
    : `fiscalYears/${fiscalYear.fiscalYearId}/payPeriods/${payPeriod.payPeriodId}/`

  switch (currentSubModuleKey) {
    case SettlementSubKeys.AC.Depletion:
      path += 'depletion'
      break
    case SettlementSubKeys.AC.MiscAccruals:
      path += 'accountPayments'
      options.params = { categoryType: AccountingCategory.Accrual.value }
      break
    case SettlementSubKeys.AC.Register:
    case SettlementSubKeys.AC.BPRegister:
      path += 'journalEntryRegister'
      options.params = { byproduct: currentSubModuleKey === SettlementSubKeys.AC.BPRegister }
      break
    case SettlementSubKeys.AC.SettlementsByPayee:
      path += 'journalentrysettlementsbypayee'
      break
  }

  return await fetchData(path, options)
}

const generateCSV = (csvData, type, { label }) => {
  label = label.replaceAll(/\s/g, '_').replaceAll(/[<>:"/\\|?*.]+/g, '_')
  const csvFile = new Blob([csvData], { type: 'text/csv' })
  const parsedType = getCsvType(type)
  const date = moment().format('MM-DD-YYYY')
  saveAs(csvFile, `${label}-${parsedType}-${date}.csv`)
}

const getCsvType = (type) => {
  const exportType = SettlementCsvExportType.enums.find(e => e.details.requestType === type)
  if (!exportType) return type

  switch (exportType) {
    case SettlementCsvExportType.AccountsPayable:
      return 'AP'
    case SettlementCsvExportType.Accruals:
      return 'AC'
    case SettlementCsvExportType.AccountsReceivable:
      return 'AR'
    case SettlementCsvExportType.StatisticalJournals:
      return 'SJ'
    default:
      return type
  }
}
